/**
 * 🧭 HelpDesktopModal — Aide & Navigation CoachDIA (desktop).
 *
 * Modal large (max 1080px) · fond ivoire lumineux premium · grille 2 colonnes.
 * Blocs Owen + Parcours + RGPD mis en avant via `highlight`.
 */
import React, { useEffect } from 'react';
import { X, HelpCircle } from 'lucide-react';
import { NAVIGATION_HELP_SECTIONS, NAVIGATION_HELP_GLOSSARY, filterHelpItems } from '../../config/navigationHelp';

const T = {
  bg: '#FBF9F3', card: '#FFFFFF', ink: '#1C1147', inkSoft: '#3F356C',
  text: '#1F2937', muted: '#6B7280', mutedSoft: '#9CA3AF',
  border: '#E8E2D2', gold: '#D4AF37', goldDeep: '#B7901F', goldLight: '#F3D060',
  ivoryDeep: '#FFFCEC', violet: '#7A4FCF', violetSoft: '#F4EFFF',
};

function HighlightBlock({ highlight }) {
  if (!highlight) return null;
  return (
    <div
      className="rounded-2xl p-5 mb-4"
      style={{
        background: `linear-gradient(135deg, ${T.ivoryDeep} 0%, ${T.card} 60%, ${T.ivoryDeep} 100%)`,
        border: `1.5px solid ${T.gold}`,
        boxShadow: `0 10px 28px -12px ${T.gold}55, inset 0 1px 0 rgba(255,255,255,0.7)`,
      }}
    >
      <div className="flex items-center gap-2 mb-2">
        <span
          className="text-[10px] uppercase tracking-[0.22em] font-bold"
          style={{ color: T.goldDeep }}
        >
          {highlight.rgpd ? 'Différenciateur RGPD' : 'Point clé'}
        </span>
      </div>
      <h4
        className="text-lg mb-1"
        style={{ color: T.ink, fontFamily: 'Georgia, serif', fontWeight: 700 }}
      >
        {highlight.title}
      </h4>
      <p className="text-sm mb-3" style={{ color: T.text }}>{highlight.body}</p>
      {highlight.bullets && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          {highlight.bullets.map((b, i) => {
            const Icon = b.icon;
            return (
              <div key={i} className="flex items-start gap-2.5 p-2.5 rounded-xl" style={{ background: T.card, border: `1px solid ${T.border}` }}>
                <div
                  className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0"
                  style={{ background: T.violetSoft }}
                >
                  <Icon className="w-4 h-4" style={{ color: T.violet }} />
                </div>
                <div className="min-w-0">
                  <div className="text-xs font-bold" style={{ color: T.ink }}>{b.label}</div>
                  <div className="text-xs" style={{ color: T.muted }}>{b.desc}</div>
                </div>
              </div>
            );
          })}
        </div>
      )}
      {highlight.badge && (
        <div className="mt-3 inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold"
          style={{ background: T.ink, color: T.goldLight }}>
          {highlight.badge}
        </div>
      )}
    </div>
  );
}

function ItemCard({ item }) {
  const Icon = item.icon;
  return (
    <div
      className="p-4 rounded-xl transition-all hover:-translate-y-0.5"
      style={{
        background: T.card,
        border: `1px solid ${T.border}`,
        boxShadow: '0 2px 6px -3px rgba(28,17,71,0.08)',
      }}
      data-testid={`help-item-${item.id}`}
    >
      <div className="flex items-start gap-3 mb-2">
        <div
          className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
          style={{
            background: `linear-gradient(135deg, ${T.ivoryDeep}, ${T.card})`,
            border: `1px solid ${T.border}`,
          }}
        >
          <Icon className="w-5 h-5" style={{ color: T.goldDeep }} />
        </div>
        <h4
          className="text-sm mt-1"
          style={{ color: T.ink, fontFamily: 'Georgia, serif', fontWeight: 700 }}
        >
          {item.label}
        </h4>
      </div>
      <dl className="space-y-1.5 text-xs leading-relaxed">
        <div><dt className="inline font-semibold" style={{ color: T.goldDeep }}>Ce que c'est · </dt><dd className="inline" style={{ color: T.text }}>{item.what}</dd></div>
        <div><dt className="inline font-semibold" style={{ color: T.goldDeep }}>Utilité · </dt><dd className="inline" style={{ color: T.text }}>{item.use}</dd></div>
        <div><dt className="inline font-semibold" style={{ color: T.goldDeep }}>À utiliser quand · </dt><dd className="inline" style={{ color: T.muted }}>{item.when}</dd></div>
      </dl>
    </div>
  );
}

export default function HelpDesktopModal({ open, onClose, userContext = {} }) {
  useEffect(() => {
    if (!open) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onEsc = (e) => { if (e.key === 'Escape') onClose?.(); };
    document.addEventListener('keydown', onEsc);
    return () => {
      document.body.style.overflow = prev;
      document.removeEventListener('keydown', onEsc);
    };
  }, [open, onClose]);

  if (!open) return null;
  const sections = filterHelpItems(NAVIGATION_HELP_SECTIONS, userContext);

  return (
    <div
      data-testid="help-desktop-modal"
      className="fixed inset-0 z-[10005] flex items-center justify-center p-4"
      style={{ background: 'rgba(28,17,71,0.55)', backdropFilter: 'blur(10px)' }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose?.(); }}
    >
      <div
        className="relative w-full max-w-5xl max-h-[92vh] rounded-3xl overflow-hidden flex flex-col"
        style={{
          background: T.bg,
          border: `1px solid ${T.border}`,
          boxShadow: '0 30px 80px -20px rgba(28,17,71,0.5), 0 0 0 1px rgba(212,175,55,0.15)',
        }}
      >
        {/* Halo or top */}
        <div
          className="absolute top-0 left-0 right-0 h-40 pointer-events-none"
          style={{ background: 'radial-gradient(ellipse at 50% 0%, rgba(243,208,96,0.35) 0%, transparent 70%)' }}
        />

        {/* Header */}
        <div
          className="flex-shrink-0 relative p-5 sm:p-7 flex items-start justify-between gap-4"
          style={{ borderBottom: `1px solid ${T.border}` }}
        >
          <div>
            <div className="flex items-center gap-2 mb-1">
              <HelpCircle className="w-4 h-4" style={{ color: T.goldDeep }} />
              <span className="text-[10px] uppercase tracking-[0.22em] font-bold" style={{ color: T.goldDeep }}>
                Aide & Navigation
              </span>
            </div>
            <h2
              className="text-2xl sm:text-3xl"
              style={{ color: T.ink, fontFamily: 'Georgia, serif', fontWeight: 600 }}
            >
              Guide de navigation CoachDIA
            </h2>
            <p className="text-sm mt-1" style={{ color: T.muted }}>
              Comprenez toute la plateforme en moins de 30 secondes.
            </p>
          </div>
          <button
            onClick={onClose}
            data-testid="help-desktop-close-btn"
            aria-label="Fermer l'aide"
            className="p-2 rounded-full transition-colors hover:bg-black/5 flex-shrink-0"
            style={{ color: T.muted }}
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Scrollable body */}
        <div className="flex-1 overflow-y-auto p-5 sm:p-7 relative">
          {sections.map((section) => (
            <section key={section.id} className="mb-9 last:mb-0">
              <div className="flex items-baseline gap-3 mb-4">
                <span className="text-2xl" aria-hidden>{section.emoji}</span>
                <div>
                  <h3
                    className="text-xl"
                    style={{ color: T.ink, fontFamily: 'Georgia, serif', fontWeight: 700 }}
                  >
                    {section.title}
                  </h3>
                  <p className="text-xs" style={{ color: T.muted }}>{section.subtitle}</p>
                </div>
              </div>
              <HighlightBlock highlight={section.highlight} />
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
                {section.items.map((it) => <ItemCard key={it.id} item={it} />)}
              </div>
            </section>
          ))}

          {/* Glossaire */}
          <section>
            <div className="flex items-baseline gap-3 mb-4">
              <span className="text-2xl" aria-hidden>🎨</span>
              <div>
                <h3 className="text-xl" style={{ color: T.ink, fontFamily: 'Georgia, serif', fontWeight: 700 }}>
                  Glossaire des icônes
                </h3>
                <p className="text-xs" style={{ color: T.muted }}>Un coup d'œil pour identifier chaque symbole.</p>
              </div>
            </div>
            <div className="grid grid-cols-4 xl:grid-cols-8 gap-2">
              {NAVIGATION_HELP_GLOSSARY.map((g) => {
                const Icon = g.icon;
                return (
                  <div
                    key={g.label}
                    className="p-3 rounded-xl text-center"
                    style={{ background: T.card, border: `1px solid ${T.border}` }}
                  >
                    <Icon className="w-5 h-5 mx-auto mb-1" style={{ color: T.goldDeep }} />
                    <div className="text-xs font-bold" style={{ color: T.ink }}>{g.label}</div>
                    <div className="text-[10px]" style={{ color: T.muted }}>{g.hint}</div>
                  </div>
                );
              })}
            </div>
          </section>
        </div>

        {/* Footer */}
        <div
          className="flex-shrink-0 px-5 sm:px-7 py-3 flex items-center justify-between gap-3"
          style={{ borderTop: `1px solid ${T.border}`, background: T.bg }}
        >
          <p className="text-[11px]" style={{ color: T.muted }}>
            Appuyez sur <kbd className="px-1.5 py-0.5 rounded text-[10px] font-mono"
              style={{ background: T.card, border: `1px solid ${T.border}`, color: T.ink }}>Esc</kbd> pour fermer.
          </p>
          <button
            onClick={onClose}
            data-testid="help-desktop-footer-close"
            className="px-5 py-2 rounded-full text-xs font-bold"
            style={{
              background: `linear-gradient(135deg, ${T.goldDeep}, ${T.gold}, ${T.goldLight})`,
              color: T.ink,
            }}
          >
            J'ai compris
          </button>
        </div>
      </div>
    </div>
  );
}
